import React, { useMemo } from 'react'
import GradingState from '@/components/organisms/Grading/GradingState'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'

interface GradingFillBlankProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
}

type FillBlankStatus = 'grading' | 'correct' | 'word_incorrect'

type FillBlankCharStatus = {
  char: string
  display: string
  status: 'correct' | 'incorrect' | 'missing'
}

interface FillBlankParsed {
  sentence: string
  translation: string
  audioUrl: string | null
  prefix: string
  suffix: string
  perCharStatus: FillBlankCharStatus[]
  extraChars: string[]
  accuracy: number | null
  gapRate: number | null
  status: FillBlankStatus
}

const parseResponse = (raw?: string) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const firstKey = Object.keys(parsed)[0];
      if (firstKey && /^\d+$/.test(firstKey)) {
        return parsed[firstKey] ?? {};
      }
    }
    return parsed;
  } catch {
    return {};
  }
};

const GradingFillBlank = ({ activity, responseJson }: GradingFillBlankProps) => {
  const parsed = useMemo<FillBlankParsed>(() => {
    const firstItem = activity?.items?.[0];
    let content: any = {};
    try {
      if (firstItem?.contentJson) {
        content = typeof firstItem.contentJson === 'string' ? JSON.parse(firstItem.contentJson) : firstItem.contentJson;
      }
    } catch (e) {
      console.error('GradingFillBlank contentJson 파싱 실패', e);
    }

    const response = parseResponse(responseJson);
    const sentence: string = typeof content.sentence === 'string'
      ? content.sentence
      : typeof content.text === 'string'
        ? content.text
        : '';
    const translation: string = typeof content.translation === 'string'
      ? content.translation
      : typeof content.korean === 'string'
        ? content.korean
        : '';
    const audioUrl: string | null = typeof content.audioUrl === 'string'
      ? content.audioUrl
      : typeof content.audio === 'string'
        ? content.audio
        : null;
    const blankSource = content.blankWords ?? content.blank ?? '';
    const blankTextBase: string = typeof blankSource === 'string' ? blankSource : '';

    const studentAnswer: string = response.inputText || response.answer || response.userInput || '';
    const blankIndex = blankTextBase && sentence ? sentence.indexOf(blankTextBase) : -1;
    const prefix = blankIndex >= 0 ? sentence.slice(0, blankIndex) : sentence;
    let suffix = blankIndex >= 0 ? sentence.slice(blankIndex + blankTextBase.length) : '';
    let blankText = blankTextBase;
    if (blankTextBase && suffix.startsWith('.') && !blankTextBase.endsWith('.')) {
      const dotMatch = suffix.match(/^\.+/);
      if (dotMatch) {
        blankText = `${blankTextBase}${dotMatch[0]}`;
        suffix = suffix.slice(dotMatch[0].length);
      }
    }

    const targetChars: string[] = blankText.split('');
    const answerChars: string[] = studentAnswer.split('');
    const perCharStatus: FillBlankCharStatus[] = targetChars.map<FillBlankCharStatus>((targetChar, idx) => {
      const userChar = answerChars[idx];
      if (!userChar) {
        return { char: ' ', display: ' ', status: 'missing' as const };
      }
      return {
        char: userChar,
        display: userChar,
        status: userChar === targetChar ? ('correct' as const) : ('incorrect' as const)
      };
    });
    const extraChars: string[] = answerChars.slice(targetChars.length);
    const matched = perCharStatus.filter((item) => item.status === 'correct').length;
    const accuracy = targetChars.length ? Math.round((matched / targetChars.length) * 100) : null;
    const gapRate = accuracy !== null ? Math.max(0, 100 - accuracy) : null;
    const status: FillBlankStatus =
      !studentAnswer
        ? 'grading'
        : matched === targetChars.length && targetChars.length > 0
          ? 'correct'
          : 'word_incorrect';

    return {
      sentence,
      translation,
      audioUrl,
      prefix,
      suffix,
      perCharStatus,
      extraChars,
      accuracy,
      gapRate,
      status
    };
  }, [activity, responseJson]);

  const renderBlankResult = () => (
    <span className='flex flex-wrap gap-1 text-2xl font-bold justify-center'>
      {parsed.perCharStatus.map((part, idx) => (
        <span
          key={`char-${idx}`}
          className={
            part.status === 'correct'
              ? 'text-green-600 border-b-2 border-green-400 px-1'
              : part.status === 'incorrect'
                ? 'text-red-600 border-b-2 border-red-400 px-1'
                : 'text-gray-400 border-b-2 border-gray-200 px-1'
          }
        >
          {part.display.trim() ? part.display : ' '}
        </span>
      ))}
      {parsed.extraChars.map((char, idx) => (
        <span key={`extra-${idx}`} className='text-red-600 border-b-2 border-red-400 px-1'>
          {char}
        </span>
      ))}
      {!parsed.perCharStatus.length && <span className='text-gray-400'>학생 입력이 없습니다.</span>}
    </span>
  );

  return (
    <div>
      <div className='flex flex-col gap-2'>
        <p className='text-lg font-bold'>{activity?.title || '빈칸 채우기'}</p>
        <div className='relative flex flex-col gap-2 justify-center items-center p-5 rounded-2xl border border-gray-200'>
          <p className='text-2xl font-bold text-center whitespace-pre-wrap'>{parsed.sentence || '문장이 없습니다.'}</p>
          {parsed.translation && <span className='text-sm text-gray-500 text-center'>{parsed.translation}</span>}
          {parsed.audioUrl && (
            <ListenButton size={40} className='absolute top-2 right-2 !p-1' audioSrc={parsed.audioUrl} />
          )}
        </div>

        <div
          className={`overflow-hidden rounded-2xl border ${
            parsed.status === 'correct'
              ? 'border-green-500'
              : parsed.status === 'word_incorrect'
                ? 'border-red-500'
                : 'border-gray-200'
          }`}
        >
          <div className='flex justify-between items-center px-4 py-2 w-full'>
            <p className='text-sm font-bold text-gray-500'>입력한 답</p>
            <GradingState status={parsed.status} />
          </div>
          <div className='flex flex-wrap justify-center items-center gap-2 px-5 py-4 bg-gray-50 text-2xl font-bold'>
            {parsed.prefix && <span>{parsed.prefix}</span>}
            {renderBlankResult()}
            {parsed.suffix && <span>{parsed.suffix}</span>}
          </div>
        </div>

        {parsed.gapRate !== null && (
          <div className='grid gap-3 mt-4 sm:grid-cols-2'>
            <div className='p-4 bg-gray-50 rounded-2xl border border-gray-100'>
              <p className='text-sm text-gray-500'>정답 일치율</p>
              <p className='text-2xl font-bold text-indigo-600'>{parsed.accuracy}%</p>
            </div>
            <div className='p-4 bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]'>
              <p className='text-sm text-gray-500'>정답 괴리율</p>
              <p className='text-2xl font-bold text-rose-500'>{parsed.gapRate}%</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GradingFillBlank